import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import UserIdentityPreview from "components/UserIdentityPreview";
import { MarketingCallStatusSelect } from "components/MarketingCallStatus";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";
import UserSubscriptionAccess from "./UserSubscriptionAccess";

const userDetailsCardSx = {
  height: "100%",
  background: "linear-gradient(145deg, #202940 0%, #1B2438 100%)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
};

const insetPanelSx = {
  backgroundColor: "rgba(255, 255, 255, 0.045)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
};

const readableBodySx = {
  color: "rgba(255, 255, 255, 0.78)",
  lineHeight: 1.65,
  overflowWrap: "anywhere",
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount, currency = "INR") => {
  const numericAmount = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch (error) {
    return `${currency} ${numericAmount.toFixed(2)}`;
  }
};

const formatSecondsLabel = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds || 0)));
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  if (minutes <= 0) {
    return `${secs}s`;
  }

  return `${minutes}m ${secs}s`;
};

const getUserImageUrl = (value) => {
  if (!value) {
    return "";
  }

  if (String(value).startsWith("http")) {
    return value;
  }

  return `${Constants.imageDomain}${value}`;
};

const buildPagerLabel = (pagination) => {
  if (!pagination?.totalDocs) {
    return "0 results";
  }

  const start = (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, pagination.totalDocs);
  return `${start}-${end} of ${pagination.totalDocs}`;
};

const toneToMuiColor = (tone) => {
  if (tone === "#FF6978") {
    return "error";
  }
  if (tone === "#FFB347") {
    return "warning";
  }
  return "info";
};

const SnapshotCard = ({ label, value, color }) => (
  <MDBox
    p={2}
    borderRadius="xl"
    sx={{
      ...insetPanelSx,
      minHeight: 120,
    }}
  >
    <MDTypography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.68)" }}>
      {label}
    </MDTypography>
    <MDTypography variant="h4" color={color} mt={1}>
      {value}
    </MDTypography>
  </MDBox>
);

SnapshotCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string.isRequired,
};

const TopicInsightRow = ({ item, chipColor }) => (
  <MDBox
    p={1.75}
    borderRadius="lg"
    sx={{
      ...insetPanelSx,
    }}
  >
    <MDBox display="flex" justifyContent="space-between" gap={2}>
      <MDBox>
        <MDTypography variant="button" color="white" fontWeight="medium">
          {item?.topic_name || "-"}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block">
          {item?.subject_name || "-"} • {formatSecondsLabel(item?.averageTimePerQuestionSeconds)} avg/question
        </MDTypography>
      </MDBox>
      <Chip
        label={`${Number(item?.accuracyPercent || 0).toFixed(0)}%`}
        color={chipColor}
        size="small"
      />
    </MDBox>
  </MDBox>
);

TopicInsightRow.propTypes = {
  item: PropTypes.object.isRequired,
  chipColor: PropTypes.string.isRequired,
};

const SubjectPerformanceRow = ({ item }) => (
  <MDBox
    p={1.75}
    borderRadius="lg"
    sx={{
      ...insetPanelSx,
    }}
  >
    <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
      <MDBox>
        <MDTypography variant="button" color="white" fontWeight="medium">
          {item?.subject_name || "-"}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block">
          {item?.attemptedQuestions || item?.totalQuestions || 0} attempted
        </MDTypography>
      </MDBox>
      <MDTypography variant="button" color="white" fontWeight="bold">
        {Number(item?.accuracyPercent || 0).toFixed(0)}%
      </MDTypography>
    </MDBox>
    <MDBox
      mt={1}
      sx={{
        height: 8,
        borderRadius: 999,
        backgroundColor: "rgba(148,163,184,0.2)",
        overflow: "hidden",
      }}
    >
      <MDBox
        sx={{
          width: `${Math.max(8, Math.min(100, Number(item?.accuracyPercent || 0)))}%`,
          height: "100%",
          borderRadius: 999,
          background: "linear-gradient(90deg, #2F9BFF 0%, #6A32FF 100%)",
        }}
      />
    </MDBox>
  </MDBox>
);

SubjectPerformanceRow.propTypes = {
  item: PropTypes.object.isRequired,
};

const PerformanceBars = ({ items }) => (
  <MDBox display="flex" alignItems="flex-end" gap={1.5} minHeight="220px">
    {items?.length ? (
      items.map((item) => (
        <MDBox key={item.key || item.label} flex={1} textAlign="center">
          <MDTypography variant="caption" color="text">
            {Number(item?.accuracyPercent || 0).toFixed(0)}%
          </MDTypography>
          <MDBox
            mt={1}
            sx={{
              height: 140,
              borderRadius: "18px",
              backgroundColor: "rgba(148,163,184,0.14)",
              overflow: "hidden",
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <MDBox
              sx={{
                width: "100%",
                height: `${Math.max(10, Math.min(100, Number(item?.accuracyPercent || 0)))}%`,
                borderRadius: "18px 18px 0 0",
                background: "linear-gradient(180deg, #3AA0FF 0%, #6B38FF 100%)",
              }}
            />
          </MDBox>
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            {item.label}
          </MDTypography>
        </MDBox>
      ))
    ) : (
      <MDTypography variant="button" color="text">
        No performance data yet.
      </MDTypography>
    )}
  </MDBox>
);

PerformanceBars.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
};

PerformanceBars.defaultProps = {
  items: [],
};

const InsightList = ({ title, items, chipColor }) => (
  <Card sx={userDetailsCardSx}>
    <MDBox p={2.5}>
      <MDTypography variant="h6" color="white">
        {title}
      </MDTypography>
      <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
        {items?.length ? (
          items.map((item) => (
            <MDBox
              key={`${title}-${String(
                item.topic_id || item.subject_id || item._id || item.topic_name
              )} `}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1.5}
              p={1.5}
              borderRadius="lg"
              sx={insetPanelSx}
            >
              <MDBox minWidth={0}>
                <MDTypography
                  variant="button"
                  color="white"
                  fontWeight="medium"
                  display="block"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {item.topic_name || item.subject_name || "-"}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  display="block"
                  sx={{ ...readableBodySx, fontSize: "0.72rem" }}
                >
                  {[
                    item.subject_name || null,
                    `${item.totalQuestions || 0} questions`,
                    item.averageTimePerQuestionSeconds
                      ? `${Number(item.averageTimePerQuestionSeconds).toFixed(1)} sec/question`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                </MDTypography>
              </MDBox>
              <Chip
                label={`${Number(item.accuracyPercent || 0).toFixed(0)}%`}
                color={chipColor}
                size="small"
              />
            </MDBox>
          ))
        ) : (
          <MDTypography variant="button" color="text">
            No data available yet.
          </MDTypography>
        )}
      </MDBox>
    </MDBox>
  </Card>
);

InsightList.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      topic_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
      subject_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
      topic_name: PropTypes.string,
      subject_name: PropTypes.string,
      totalQuestions: PropTypes.number,
      accuracyPercent: PropTypes.number,
      averageTimePerQuestionSeconds: PropTypes.number,
    })
  ),
  chipColor: PropTypes.string.isRequired,
};

InsightList.defaultProps = {
  items: [],
};

const FocusSignalCard = ({ signal }) => (
  <Card sx={userDetailsCardSx}>
    <MDBox p={2.5}>
      <Chip
        label={signal?.title || "Focus Signal"}
        color={toneToMuiColor(signal?.tone)}
        size="small"
        sx={{ mb: 1.5 }}
      />
      <MDTypography variant="button" color="text">
        {signal?.body || "-"}
      </MDTypography>
    </MDBox>
  </Card>
);

FocusSignalCard.propTypes = {
  signal: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    tone: PropTypes.string,
  }),
};

FocusSignalCard.defaultProps = {
  signal: null,
};

const SectionList = ({
  title,
  rows,
  fields,
  emptyText,
  pagination,
  onPageChange,
  onEntriesPerPageChange,
}) => (
  <Card sx={userDetailsCardSx}>
    <MDBox p={2.5}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={1}
      >
        <MDTypography variant="h6" color="white">
          {title}
        </MDTypography>
        <MDTypography variant="button" color="text">
          {buildPagerLabel(pagination)}
        </MDTypography>
      </MDBox>
      <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
        {rows?.length ? (
          rows.map((row, index) => (
            <MDBox
              key={row._id || `${title}-${index}`}
              p={2}
              borderRadius="xl"
              sx={insetPanelSx}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="button" color="white" fontWeight="bold">
                  #{(pagination?.page - 1 || 0) * (pagination?.limit || 10) + index + 1}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {fields[0]?.render ? fields[0].render(row) : row[fields[0]?.key] ?? "-"}
                </MDTypography>
              </MDBox>

              <Grid container spacing={1.5}>
                {fields.slice(1).map((field) => (
                  <Grid item xs={12} sm={6} md={4} key={field.key}>
                    <MDBox>
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        {field.label}
                      </MDTypography>
                      <MDTypography
                        variant="button"
                        color="white"
                        display="block"
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {field.render ? field.render(row) : row[field.key] ?? "-"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          ))
        ) : (
          <MDTypography variant="button" color="text">
            {emptyText}
          </MDTypography>
        )}
      </MDBox>
      <MDBox
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <MDBox display="flex" alignItems="center" gap={1.25}>
          <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
            Rows per page
          </MDTypography>
          <TextField
            select
            size="small"
            value={pagination?.limit || 10}
            onChange={(event) => onEntriesPerPageChange(Number(event.target.value))}
            sx={{ width: 82 }}
          >
            {[10, 25, 50, 100].map((size) => (
              <MenuItem key={size} value={size}>{size}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        {pagination?.totalPages > 1 ? (
          <Pagination
            page={pagination.page}
            count={pagination.totalPages}
            color="primary"
            variant="outlined"
            shape="rounded"
            sx={agriPaginationSx}
            onChange={(event, page) => onPageChange(page)}
          />
        ) : null}
      </MDBox>
    </MDBox>
  </Card>
);

SectionList.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
    })
  ).isRequired,
  emptyText: PropTypes.string.isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    limit: PropTypes.number,
    totalDocs: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
  onEntriesPerPageChange: PropTypes.func,
};

SectionList.defaultProps = {
  rows: [],
  pagination: null,
  onPageChange: () => {},
  onEntriesPerPageChange: () => {},
};

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionLimit, setTransactionLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [callStatusSaving, setCallStatusSaving] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("warning");
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "freemium",
    account_verified: true,
    referral_code: "",
    wallet_balance: 0,
    address: "",
    dob: "",
  });

  useEffect(() => {
    const loadUserDetails = async () => {
      setLoading(true);
      if (init() !== "success") {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${Constants.getUrls.getUserDetails}${id}`, {
          params: {
            practice_page: 1,
            practice_limit: 1,
            mock_page: 1,
            mock_limit: 1,
            test_series_page: 1,
            test_series_limit: 1,
            live_test_page: 1,
            live_test_limit: 1,
            friends_test_page: 1,
            friends_test_limit: 1,
            transaction_page: transactionPage,
            transaction_limit: transactionLimit,
          },
        });
        setDetails(response?.data?.data || null);
        const userPayload = response?.data?.data?.user || {};
        const walletPayload = response?.data?.data?.wallet || {};
        setForm({
          name: userPayload?.name || "",
          email: userPayload?.email || "",
          mobile: userPayload?.mobile || "",
          role: userPayload?.role || "freemium",
          account_verified: Boolean(userPayload?.account_verified),
          referral_code: userPayload?.referral_code || "",
          wallet_balance: Number(walletPayload?.wallet_balance || 0),
          address: userPayload?.address || "",
          dob: userPayload?.dob ? new Date(userPayload.dob).toISOString().slice(0, 10) : "",
        });
      } catch (error) {
        setNotiColor("warning");
        setMessage(error?.response?.data?.message || "Unable to load user details.");
        setNotification(true);
      } finally {
        setLoading(false);
      }
    };

    loadUserDetails();
  }, [id, transactionPage, transactionLimit]);

  const showNotification = useCallback((nextMessage, nextColor = "success") => {
    setNotiColor(nextColor);
    setMessage(nextMessage);
    setNotification(true);
  }, []);

  const openAiInsights = async () => {
    setAiDialogOpen(true);

    if (aiInsights || aiInsightsLoading) {
      return;
    }

    if (init() !== "success") {
      return;
    }

    try {
      setAiInsightsLoading(true);
      const response = await axios.get(`${Constants.getUrls.getUserAiInsights}${id}/ai-insights`);
      setAiInsights(response?.data?.data?.insights || null);
    } catch (error) {
      showNotification(
        error?.response?.data?.message || "Unable to load AI insights.",
        "error"
      );
    } finally {
      setAiInsightsLoading(false);
    }
  };

  const handleAdminUpdate = async () => {
    if (init() !== "success") {
      return;
    }

    try {
      setSaving(true);
      const response = await axios.put(`${Constants.putUrls.updateUserDetails}${id}`, {
        name: form.name,
        email: form.email,
        mobile: form.mobile,
        role: form.role,
        account_verified: form.account_verified,
        referral_code: form.referral_code,
        wallet_balance: Number(form.wallet_balance || 0),
        address: form.address,
        dob: form.dob || null,
      });

      showNotification(
        response?.data?.message || "User details updated successfully.",
        "success"
      );

      const updatedUser = response?.data?.data?.user || {};
      const updatedWallet = response?.data?.data?.wallet || {};

      setDetails((prev) => ({
        ...prev,
        user: {
          ...(prev?.user || {}),
          ...updatedUser,
        },
        wallet: updatedWallet,
      }));

      setForm((prev) => ({
        ...prev,
        name: updatedUser?.name || "",
        email: updatedUser?.email || "",
        mobile: updatedUser?.mobile || "",
        role: updatedUser?.role || "freemium",
        account_verified: Boolean(updatedUser?.account_verified),
        referral_code: updatedUser?.referral_code || "",
        address: updatedUser?.address || "",
        dob: updatedUser?.dob ? new Date(updatedUser.dob).toISOString().slice(0, 10) : "",
        wallet_balance: Number(updatedWallet?.wallet_balance || 0),
      }));
    } catch (error) {
      showNotification(
        error?.response?.data?.message || "Unable to update user details.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCallStatusChange = async (callStatus) => {
    if (init() !== "success") return;

    try {
      setCallStatusSaving(true);
      const response = await axios.put(
        `${Constants.putUrls.updateUserCallStatus}${id}/call-status`,
        { call_status: callStatus }
      );
      const updated = response?.data?.data || {};
      setDetails((current) => ({
        ...current,
        user: {
          ...(current?.user || {}),
          marketing_call_status: updated.marketing_call_status || callStatus,
          marketing_call_updated_at: updated.marketing_call_updated_at || new Date().toISOString(),
        },
      }));
      showNotification(response?.data?.message || "Call status updated successfully.");
    } catch (error) {
      showNotification(error?.response?.data?.message || "Unable to update call status.", "error");
    } finally {
      setCallStatusSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    const userName = details?.user?.name || "this user";
    if (!window.confirm(`Delete ${userName}? This will disable their account.`)) return;
    if (init() !== "success") return;

    try {
      setDeletingUser(true);
      const response = await axios.delete(`${Constants.deleteUrls.deleteUser}${id}`);
      showNotification(response?.data?.message || "User deleted successfully.");
      setTimeout(() => navigate("/users"), 700);
    } catch (error) {
      showNotification(error?.response?.data?.message || "Unable to delete user.", "error");
      setDeletingUser(false);
    }
  };

  const overviewCards = useMemo(() => {
    const overview = details?.overview || {};
    return [
      { label: "Date Joined", value: formatDate(overview.joined_at) },
      { label: "Total Points", value: overview.earned_points || details?.user?.earned_points || 0 },
      { label: "Practice Tests", value: overview.practice_tests_given || 0 },
      { label: "Mock Tests", value: overview.mock_tests_given || 0 },
      { label: "Live Tests", value: overview.live_tests_joined || 0 },
      { label: "Friends Tests", value: overview.friends_tests_joined || 0 },
      { label: "Test Series Joined", value: overview.scheduled_tests_joined || 0 },
      { label: "MCQ Solved", value: overview.total_mcq_solved || 0 },
      { label: "Practice Accuracy", value: `${overview.average_practice_accuracy || 0}%` },
      { label: "Successful Payments", value: overview.successful_transactions || 0 },
      {
        label: "Total Paid",
        value: formatCurrency(overview.total_amount_paid || 0),
      },
    ];
  }, [details]);

  const aiSnapshotCards = useMemo(
    () => [
      {
        key: "attempted",
        label: "Total Attempted",
        value: aiInsights?.total_attempted || 0,
        color: "info",
      },
      {
        key: "skipped",
        label: "Skipped",
        value: aiInsights?.total_skipped || 0,
        color: "warning",
      },
      {
        key: "correct",
        label: "Correct",
        value: aiInsights?.total_correct || 0,
        color: "success",
      },
      {
        key: "incorrect",
        label: "Wrong",
        value: aiInsights?.total_incorrect || 0,
        color: "error",
      },
    ],
    [aiInsights]
  );

  const aiSpeedCards = useMemo(
    () => [
      {
        key: "avg-speed",
        label: "Average Speed",
        value: formatSecondsLabel(aiInsights?.speed_overview?.overall_average_time_per_question_seconds),
        helper:
          aiInsights?.speed_overview?.speed_band === "slow"
            ? "Careful but slow"
            : aiInsights?.speed_overview?.speed_band === "fast"
            ? "Fast response pattern"
            : "Balanced pace",
        color: "#58A6FF",
      },
      {
        key: "slow-correct",
        label: "Slow But Correct",
        value: aiInsights?.speed_accuracy_insights?.slow_but_correct?.[0]?.topic_name || "None",
        helper: aiInsights?.speed_accuracy_insights?.slow_but_correct?.[0]
          ? `${formatSecondsLabel(
              aiInsights.speed_accuracy_insights.slow_but_correct[0]
                .averageTimePerQuestionSeconds
            )} avg/question`
          : "No topic in this pattern",
        color: "#2BB7A9",
      },
      {
        key: "fast-risky",
        label: "Fast But Risky",
        value: aiInsights?.speed_accuracy_insights?.fast_but_risky?.[0]?.topic_name || "None",
        helper: aiInsights?.speed_accuracy_insights?.fast_but_risky?.[0]
          ? `${aiInsights.speed_accuracy_insights.fast_but_risky[0].accuracyPercent}% accuracy`
          : "No risky fast pattern",
        color: "#FF9D42",
      },
      {
        key: "strong-topics",
        label: "Strong Topics",
        value: aiInsights?.topic_intelligence_matrix?.strong_topics?.length || 0,
        helper: "High accuracy + good speed",
        color: "#45C46A",
      },
      {
        key: "needs-speed",
        label: "Needs Speed",
        value: aiInsights?.topic_intelligence_matrix?.slow_strong_topics?.length || 0,
        helper: "High accuracy + slow speed",
        color: "#4A7DFF",
      },
      {
        key: "weak-topics",
        label: "Weak Topics",
        value: aiInsights?.topic_intelligence_matrix?.weak_topics?.length || 0,
        helper: "Low accuracy + slow speed",
        color: "#FF6978",
      },
    ],
    [aiInsights]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              User Details
            </MDTypography>
            <Link to="/users">
              <MDButton variant="contained" size="small" color="dark">
                Back to Users
              </MDButton>
            </Link>
          </MDBox>

          <MDBox p={3}>
            {loading ? (
              <MDBox py={6} textAlign="center">
                <MDTypography variant="h5" color="white">
                  Loading user details...
                </MDTypography>
              </MDBox>
            ) : !details ? (
              <MDBox py={6} textAlign="center">
                <MDTypography variant="h6" color="white">
                  No user details found.
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <Card sx={{ ...userDetailsCardSx, mb: 3 }}>
                  <MDBox p={2.5}>
                    <Grid container spacing={2.5} alignItems="center">
                      <Grid item xs={12} md={2}>
                        <MDBox
                          display="flex"
                          justifyContent={{ xs: "flex-start", md: "center" }}
                          alignItems="center"
                          height="100%"
                        >
                          <UserIdentityPreview
                            name={details?.user?.name || "User"}
                            imageUrl={getUserImageUrl(details?.user?.profile_pic_url)}
                            imageSize={116}
                            showText={false}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={5} minWidth={0}>
                        <MDTypography
                          variant="h4"
                          color="white"
                          sx={{ overflowWrap: "anywhere", lineHeight: 1.25 }}
                        >
                          {details?.user?.name || "-"}
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          display="block"
                          sx={readableBodySx}
                        >
                          {details?.user?.email || "No email added"}
                        </MDTypography>
                        <MDTypography display="block" variant="button" sx={readableBodySx}>
                          Mobile: {details?.user?.mobile || "-"}
                        </MDTypography>
                        <MDTypography display="block" variant="button" sx={readableBodySx}>
                          College:{" "}
                          {details?.user?.university?.short_name ||
                            details?.user?.university?.name ||
                            "-"}
                        </MDTypography>
                        <MDTypography display="block" variant="button" sx={readableBodySx}>
                          Total Points: {details?.overview?.earned_points || details?.user?.earned_points || 0}
                        </MDTypography>
                        <MDTypography display="block" variant="button" sx={readableBodySx}>
                          Role: {details?.user?.role || "-"}
                        </MDTypography>
                        <MDTypography display="block" variant="button" sx={readableBodySx}>
                          Referral Code: {details?.user?.referral_code || "-"}
                        </MDTypography>
                        <MDTypography display="block" variant="button" sx={readableBodySx}>
                          Wallet: {formatCurrency(details?.wallet?.wallet_balance || 0)}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <MDBox
                          display="flex"
                          justifyContent={{ xs: "flex-start", md: "flex-end" }}
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                        >
                          <MDButton variant="contained" color="info" onClick={openAiInsights}>
                            View AI Insight
                          </MDButton>
                          <MDButton
                            component={Link}
                            to={`/user-practice-tests/${id}`}
                            variant="outlined"
                            color="info"
                            sx={{ color: "#90CAF9 !important", borderColor: "#58A6FF !important" }}
                          >
                            View Practice Tests
                          </MDButton>
                          <MDButton
                            component={Link}
                            to={`/user-details/${id}/feedback`}
                            variant="contained"
                            color="success"
                          >
                            Feedback
                          </MDButton>
                          <MDBox display="flex" alignItems="center" gap={0.75}>
                            <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.72)" }}>
                              Call:
                            </MDTypography>
                            <MarketingCallStatusSelect
                              status={details?.user?.marketing_call_status}
                              disabled={callStatusSaving}
                              onChange={handleCallStatusChange}
                              ariaLabel={`Call status for ${details?.user?.name || "user"}`}
                            />
                          </MDBox>
                          <MDButton
                            variant="outlined"
                            color="error"
                            disabled={deletingUser}
                            onClick={handleDeleteUser}
                          >
                            {deletingUser ? "Deleting..." : "Delete User"}
                          </MDButton>
                          <Chip
                            label={details?.user?.account_verified ? "Verified" : "Not Verified"}
                            color={details?.user?.account_verified ? "success" : "warning"}
                          />
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>

                <Grid container spacing={2} mb={1}>
                  {overviewCards.map((item) => (
                    <Grid item xs={12} sm={6} md={3} key={item.label}>
                      <Card sx={userDetailsCardSx}>
                        <MDBox
                          p={2.25}
                          minHeight={{ xs: 112, md: 132 }}
                          display="flex"
                          flexDirection="column"
                          justifyContent="space-between"
                        >
                          <MDTypography
                            variant="button"
                            sx={{ color: "rgba(255, 255, 255, 0.68)" }}
                          >
                            {item.label}
                          </MDTypography>
                          <MDTypography
                            variant="h5"
                            color="white"
                            mt={1}
                            sx={{
                              fontSize: { xs: "1.2rem", lg: "1.35rem" },
                              lineHeight: 1.3,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {item.value}
                          </MDTypography>
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <UserSubscriptionAccess userId={id} onNotify={showNotification} />

                <Grid container spacing={2} mt={1} mb={1}>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          {details?.ai_analysis?.ai_primary_insight?.title || "AI Insight"}
                        </MDTypography>
                        <MDBox mt={1.5}>
                          <MDTypography variant="button" display="block" sx={readableBodySx}>
                            {details?.ai_analysis?.ai_primary_insight?.message ||
                              "No AI insight available yet."}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          Recommended Practice Plan
                        </MDTypography>
                        <MDBox mt={1.5} display="flex" flexDirection="column" gap={1}>
                          <MDTypography variant="button" color="white" fontWeight="medium">
                            {details?.ai_analysis?.practice_plan?.topicName || "Smart Practice"}
                            {" • "}
                            {details?.ai_analysis?.practice_plan?.subjectName || "AgriQuiz"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {details?.ai_analysis?.practice_plan?.questionCount || 0} questions
                            {" • "}
                            {details?.ai_analysis?.practice_plan?.minutes || 0} minutes
                            {" • "}
                            Priority: {details?.ai_analysis?.practice_plan?.priority || "Medium"}
                          </MDTypography>
                          <MDTypography variant="button" sx={readableBodySx}>
                            {details?.ai_analysis?.practice_plan?.reason ||
                              "No practice plan available yet."}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={2} mt={1}>
                  {(details?.ai_analysis?.ai_focus_signals || []).map((signal) => (
                    <Grid item xs={12} md={4} key={signal.key || signal.title}>
                      <FocusSignalCard signal={signal} />
                    </Grid>
                  ))}
                </Grid>

                <Card sx={{ ...userDetailsCardSx, mt: 3, mb: 3 }}>
                  <MDBox p={2.5}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={1}
                      mb={2}
                    >
                      <MDTypography variant="h6" color="white">
                        Edit User Details
                      </MDTypography>
                      <MDButton
                        variant="contained"
                        color="info"
                        disabled={saving}
                        onClick={handleAdminUpdate}
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </MDButton>
                    </MDBox>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Full Name"
                          value={form.name}
                          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={form.email}
                          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Mobile Number"
                          value={form.mobile}
                          onChange={(e) => setForm((prev) => ({ ...prev, mobile: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Referral Code"
                          value={form.referral_code}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              referral_code: e.target.value.toUpperCase(),
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Wallet Balance"
                          type="number"
                          value={form.wallet_balance}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, wallet_balance: e.target.value }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Role</InputLabel>
                          <Select
                            value={form.role}
                            label="Role"
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, role: e.target.value }))
                            }
                          >
                            <MenuItem value="freemium">Freemium</MenuItem>
                            <MenuItem value="premium">Premium</MenuItem>
                            <MenuItem value="agriquiz_scholar">AgriQuiz Scholar</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Account Verified</InputLabel>
                          <Select
                            value={form.account_verified ? "yes" : "no"}
                            label="Account Verified"
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                account_verified: e.target.value === "yes",
                              }))
                            }
                          >
                            <MenuItem value="yes">Verified</MenuItem>
                            <MenuItem value="no">Not Verified</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Date of Birth"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          value={form.dob}
                          onChange={(e) => setForm((prev) => ({ ...prev, dob: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Address"
                          value={form.address}
                          onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>

                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} md={4}>
                    <InsightList
                      title="Strong Topics"
                      items={details?.ai_analysis?.strong_topics || []}
                      chipColor="success"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InsightList
                      title="Needs Practice"
                      items={details?.ai_analysis?.needs_practice_topics || []}
                      chipColor="warning"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InsightList
                      title="Weak Topics"
                      items={details?.ai_analysis?.weak_topics || []}
                      chipColor="error"
                    />
                  </Grid>
                </Grid>

                <MDBox mt={3}>
                  <SectionList
                    title="Transactions"
                    rows={details?.transactions || []}
                    pagination={details?.transactions_pagination}
                    onPageChange={setTransactionPage}
                    onEntriesPerPageChange={(limit) => {
                      setTransactionLimit(limit);
                      setTransactionPage(1);
                    }}
                    emptyText="No transactions found yet."
                    fields={[
                      {
                        key: "createdAt",
                        label: "Date",
                        render: (row) => formatDate(row.createdAt),
                      },
                      {
                        key: "test_series_name",
                        label: "Purpose",
                        render: (row) => row.test_series_name || "-",
                      },
                      {
                        key: "amount",
                        label: "Amount",
                        render: (row) => formatCurrency(row.amount, row.currency),
                      },
                      { key: "payment_status", label: "Payment Status" },
                      {
                        key: "gateway_status",
                        label: "Gateway Status",
                        render: (row) => row.gateway_status || "-",
                      },
                      {
                        key: "transaction_id",
                        label: "Txn Id",
                        render: (row) => row.transaction_id || "-",
                      },
                    ]}
                  />
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <Dialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        fullWidth
        maxWidth="xl"
      >
        <DialogContent sx={{ p: 0, backgroundColor: "#07101F" }}>
          <MDBox
            px={3}
            py={2.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background:
                "radial-gradient(circle at top left, rgba(50,119,255,0.45), transparent 38%), linear-gradient(135deg, #0B1223 0%, #131D37 52%, #0A1326 100%)",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <MDBox>
              <MDTypography variant="h4" color="white">
                AgriMentor AI
              </MDTypography>
              <MDTypography variant="button" color="white">
                {details?.user?.name || "User"} performance intelligence
              </MDTypography>
            </MDBox>
            <IconButton onClick={() => setAiDialogOpen(false)} sx={{ color: "#ffffff" }}>
              <CloseIcon />
            </IconButton>
          </MDBox>

          <MDBox p={3}>
            {aiInsightsLoading ? (
              <Card sx={userDetailsCardSx}>
                <MDBox p={4} textAlign="center">
                  <MDTypography variant="h5" color="white">
                    Loading AI insights...
                  </MDTypography>
                </MDBox>
              </Card>
            ) : !aiInsights ? (
              <Card sx={userDetailsCardSx}>
                <MDBox p={4} textAlign="center">
                  <MDTypography variant="h6" color="white">
                    No AI insights available yet.
                  </MDTypography>
                </MDBox>
              </Card>
            ) : (
              <MDBox display="flex" flexDirection="column" gap={3}>
                <Card>
                  <MDBox
                    p={3}
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(16,25,48,1) 0%, rgba(20,33,62,0.92) 100%)",
                    }}
                  >
                    <MDTypography variant="h6" color="white">
                      Recommended Focus
                    </MDTypography>
                    <MDTypography variant="button" color="white" mt={1} display="block">
                      {aiInsights?.recommended_topic?.reason ||
                        "Focus on weaker topics to improve score and speed."}
                    </MDTypography>
                  </MDBox>
                </Card>

                <Grid container spacing={2}>
                  {aiSnapshotCards.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.key}>
                      <SnapshotCard label={card.label} value={card.value} color={card.color} />
                    </Grid>
                  ))}
                </Grid>

                <Card>
                  <MDBox
                    p={3}
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(16,35,64,1) 0%, rgba(27,49,89,0.96) 100%)",
                    }}
                  >
                    <MDTypography variant="h6" color="white">
                      {aiInsights?.ai_primary_insight?.title || "AI Insight"}
                    </MDTypography>
                    <MDTypography variant="button" color="white" mt={1} display="block">
                      {aiInsights?.ai_primary_insight?.message ||
                        "Use time and accuracy together to shape better practice."}
                    </MDTypography>
                  </MDBox>
                </Card>

                <Card sx={userDetailsCardSx}>
                  <MDBox p={2.5}>
                    <MDTypography variant="h6" color="white">
                      AI Focus Signals
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      These are the patterns that need immediate attention.
                    </MDTypography>
                    <Grid container spacing={2} mt={0.5}>
                      {(aiInsights?.ai_focus_signals || []).length ? (
                        (aiInsights?.ai_focus_signals || []).map((signal) => (
                          <Grid item xs={12} md={4} key={signal.key || signal.title}>
                            <FocusSignalCard signal={signal} />
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <MDTypography variant="button" color="text">
                            Solve more practice tests to unlock sharper AI signals.
                          </MDTypography>
                        </Grid>
                      )}
                    </Grid>
                  </MDBox>
                </Card>

                <Card sx={userDetailsCardSx}>
                  <MDBox p={2.5}>
                    <MDTypography variant="h6" color="white">
                      Speed vs Accuracy
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      The same speed-and-score patterns shown in the app.
                    </MDTypography>
                    <Grid container spacing={2} mt={0.5}>
                      {aiSpeedCards.map((card) => (
                        <Grid item xs={12} sm={6} md={4} key={card.key}>
                          <MDBox
                            p={2}
                            borderRadius="xl"
                            sx={{
                              ...insetPanelSx,
                              minHeight: 138,
                            }}
                          >
                            <MDTypography variant="caption" sx={{ color: card.color }}>
                              {card.label}
                            </MDTypography>
                            <MDTypography variant="h6" color="white" mt={1}>
                              {card.value}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block" mt={1}>
                              {card.helper}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                </Card>

                <Card sx={userDetailsCardSx}>
                  <MDBox p={2.5}>
                    <MDTypography variant="h6" color="white">
                      Today&apos;s AI Practice Plan
                    </MDTypography>
                    <Grid container spacing={2} mt={0.5} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="h5" color="white">
                          {aiInsights?.practice_plan?.topicName || "Smart Practice"}
                        </MDTypography>
                        <MDTypography variant="button" color="text">
                          {aiInsights?.practice_plan?.subjectName || "AgriQuiz"}
                        </MDTypography>
                        <MDTypography variant="button" color="text" display="block" mt={1}>
                          {aiInsights?.practice_plan?.reason || "No plan available yet."}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <SnapshotCard
                              label="Questions"
                              value={aiInsights?.practice_plan?.questionCount || 0}
                              color="info"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <SnapshotCard
                              label="Minutes"
                              value={aiInsights?.practice_plan?.minutes || 0}
                              color="success"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <SnapshotCard
                              label="Priority"
                              value={aiInsights?.practice_plan?.priority || "Medium"}
                              color="warning"
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          Weak Topics
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Slow and low-accuracy topics that need priority practice.
                        </MDTypography>
                        <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
                          {(aiInsights?.weak_topics || []).length ? (
                            aiInsights.weak_topics.map((item) => (
                              <TopicInsightRow
                                key={`weak-${String(item.topic_id || item.topic_name)}`}
                                item={item}
                                chipColor="error"
                              />
                            ))
                          ) : (
                            <MDTypography variant="button" color="text">
                              No weak topics yet.
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          Strong Topics
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          High accuracy topics where speed is already under control.
                        </MDTypography>
                        <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
                          {(aiInsights?.strong_topics || []).length ? (
                            aiInsights.strong_topics.map((item) => (
                              <TopicInsightRow
                                key={`strong-${String(item.topic_id || item.topic_name)}`}
                                item={item}
                                chipColor="success"
                              />
                            ))
                          ) : (
                            <MDTypography variant="button" color="text">
                              No strong topics yet.
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          Risky Fast Answers
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Topics where speed is high but accuracy suggests rushing.
                        </MDTypography>
                        <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
                          {(aiInsights?.speed_accuracy_insights?.fast_but_risky || []).length ? (
                            aiInsights.speed_accuracy_insights.fast_but_risky.map((item) => (
                              <TopicInsightRow
                                key={`risky-${String(item.topic_id || item.topic_name)}`}
                                item={item}
                                chipColor="warning"
                              />
                            ))
                          ) : (
                            <MDTypography variant="button" color="text">
                              No risky fast-answer topics right now.
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          Most Skipped Topics
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Skips usually indicate confusion, hesitation, or low confidence.
                        </MDTypography>
                        <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
                          {(aiInsights?.skipped_topics || []).length ? (
                            aiInsights.skipped_topics.map((item) => (
                              <TopicInsightRow
                                key={`skip-${String(item.topic_id || item.topic_name)}`}
                                item={item}
                                chipColor="warning"
                              />
                            ))
                          ) : (
                            <MDTypography variant="button" color="text">
                              Great sign. Not skipping many questions right now.
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          Strong Subjects
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Subjects currently giving the best practice accuracy.
                        </MDTypography>
                        <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
                          {(aiInsights?.strong_subjects || []).length ? (
                            aiInsights.strong_subjects.map((item) => (
                              <SubjectPerformanceRow
                                key={`subject-${String(item.subject_id || item.subject_name)}`}
                                item={item}
                              />
                            ))
                          ) : (
                            <MDTypography variant="button" color="text">
                              No strong-subject pattern yet.
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          Needs Speed
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Topics mostly understood, but still taking too long.
                        </MDTypography>
                        <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
                          {(aiInsights?.topic_intelligence_matrix?.slow_strong_topics || [])
                            .length ? (
                            aiInsights.topic_intelligence_matrix.slow_strong_topics.map((item) => (
                              <TopicInsightRow
                                key={`slow-${String(item.topic_id || item.topic_name)}`}
                                item={item}
                                chipColor="info"
                              />
                            ))
                          ) : (
                            <MDTypography variant="button" color="text">
                              No slow-strong topics yet.
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          Monthly Performance
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Accuracy trend across recent months.
                        </MDTypography>
                        <MDBox mt={2}>
                          <PerformanceBars items={aiInsights?.monthly_performance || []} />
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={userDetailsCardSx}>
                      <MDBox p={2.5}>
                        <MDTypography variant="h6" color="white">
                          This Month Weekly Performance
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Weekly accuracy trend for the current month.
                        </MDTypography>
                        <MDBox mt={2}>
                          <PerformanceBars
                            items={aiInsights?.this_month_weekly_performance || []}
                          />
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
      </Dialog>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime="Just now"
        open={notification}
      />
    </DashboardLayout>
  );
}
